import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useMessagingSocket } from '@/hooks/useMessagingSocket';
import { useConversation } from '@/hooks/useMessages';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { useMessagingStore } from '@/store/messaging.store';
import { API_URL } from '@/services/api';
import { toDateTimeLocal } from '@/utils/date';
import { toast } from '../common/Toast';

import Button, { IconButton } from '../common/Button';
import Input from '../common/Input';
import SearchInput from '../common/SearchInput';
import FaIcon from '../common/FaIcon';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


interface MessageInputProps {
  conversationId: string;
  droppedFiles?: File[];
  onDroppedFilesProcessed?: () => void;
}

interface User {
  id: string;
  name: string;
  email: string;
}

interface GifResult {
  id: string;
  title: string;
  preview_url: string;
  url: string;
}

// Tenor API key - must be set via VITE_TENOR_API_KEY environment variable
const TENOR_API_KEY = import.meta.env.VITE_TENOR_API_KEY;

export function MessageInput({ conversationId, droppedFiles, onDroppedFilesProcessed }: MessageInputProps) {
  const [content, setContent] = useState('');
  const isTypingRef = useRef(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionedUsers, setMentionedUsers] = useState<Map<string, { id: string; name: string }>>(new Map());
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState('');
  const [gifs, setGifs] = useState<GifResult[]>([]);
  const [isLoadingGifs, setIsLoadingGifs] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<string>('');

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const gifPickerRef = useRef<HTMLDivElement>(null);
  const gifSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recordingCancelledRef = useRef(false);
  const schedulePickerRef = useRef<HTMLDivElement>(null);

  const { sendMessage, startTyping, stopTyping } = useMessagingSocket();
  const { token, user } = useAuthStore();
  const { activeTenant } = useTenantStore();
  const {
    addMessage,
    replyingTo,
    clearReplyingTo,
    drafts,
    setDraft,
    clearDraft,
    addScheduledMessage,
    scheduledMessages,
    removeScheduledMessage,
  } = useMessagingStore();

  const replyMessage = replyingTo[conversationId];

  // Load draft when switching conversations (reset content if no draft exists)
  useEffect(() => {
    const draft = drafts[conversationId];
    setContent(draft || '');
    // Clear mention state from the previous conversation
    setMentionedUsers(new Map());
    setShowMentions(false);
    setMentionQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when conversationId changes
  }, [conversationId]);

  // Save draft on content change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (content.trim()) {
        setDraft(conversationId, content);
      } else {
        clearDraft(conversationId);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [content, conversationId, setDraft, clearDraft]);

  // Get conversation participants for @mentions
  const { data: conversation } = useConversation(conversationId);

  const participantUsers = useMemo(() => {
    if (!conversation?.participants) return [];
    return conversation.participants
      .filter((p) => p.user && !p.left_at)
      .map((p): User => ({ id: p.user!.id, name: p.user!.name, email: p.user!.email }));
  }, [conversation?.participants]);

  // Filter users for mention autocomplete
  const filteredMentionUsers = useMemo(() => {
    if (!mentionQuery) return participantUsers.filter(u => u.id !== user?.id).slice(0, 5);
    const query = mentionQuery.toLowerCase();
    return participantUsers
      .filter(u => u.id !== user?.id && u.name.toLowerCase().includes(query))
      .slice(0, 5);
  }, [participantUsers, mentionQuery, user?.id]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [content]);

  // Close pickers on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (gifPickerRef.current && !gifPickerRef.current.contains(e.target as Node)) {
        setShowGifPicker(false);
      }
      if (schedulePickerRef.current && !schedulePickerRef.current.contains(e.target as Node)) {
        setShowSchedulePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search GIFs using Tenor API
  const searchGifs = useCallback(async (query: string) => {
    setIsLoadingGifs(true);
    try {
      const endpoint = query.trim()
        ? `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(query)}&key=${TENOR_API_KEY}&limit=20&media_filter=gif,tinygif`
        : `https://tenor.googleapis.com/v2/featured?key=${TENOR_API_KEY}&limit=20&media_filter=gif,tinygif`;

      const response = await fetch(endpoint);
      const data = await response.json();

      const results: GifResult[] = (data.results || []).map((item: { id: string; title: string; media_formats: { tinygif?: { url: string }; gif?: { url: string } } }) => ({
        id: item.id,
        title: item.title,
        preview_url: item.media_formats?.tinygif?.url || item.media_formats?.gif?.url,
        url: item.media_formats?.gif?.url || item.media_formats?.tinygif?.url,
      }));

      setGifs(results);
    } catch (error) {
      console.error('Failed to fetch GIFs:', error);
      setGifs([]);
    } finally {
      setIsLoadingGifs(false);
    }
  }, []);

  // Load featured GIFs when picker opens
  useEffect(() => {
    if (showGifPicker && gifs.length === 0) {
      searchGifs('');
    }
  }, [showGifPicker, gifs.length, searchGifs]);

  // Debounced GIF search
  useEffect(() => {
    if (!showGifPicker) return;

    if (gifSearchTimeoutRef.current) {
      clearTimeout(gifSearchTimeoutRef.current);
    }

    gifSearchTimeoutRef.current = setTimeout(() => {
      searchGifs(gifSearch);
    }, 300);

    return () => {
      if (gifSearchTimeoutRef.current) {
        clearTimeout(gifSearchTimeoutRef.current);
      }
    };
  }, [gifSearch, showGifPicker, searchGifs]);

  // Handle typing indicator
  // Use a ref to track which conversation the pending timeout belongs to,
  // so the cleanup effect won't accidentally clear a timeout for a new conversation.
  const typingConvIdRef = useRef<string | null>(null);

  const handleTyping = useCallback(() => {
    // If we were typing in a different conversation, stop that first
    if (isTypingRef.current && typingConvIdRef.current && typingConvIdRef.current !== conversationId) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      stopTyping(typingConvIdRef.current);
      isTypingRef.current = false;
      typingConvIdRef.current = null;
    }

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      startTyping(conversationId);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingConvIdRef.current = conversationId;
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      typingConvIdRef.current = null;
      stopTyping(conversationId);
    }, 3000);
  }, [conversationId, startTyping, stopTyping]);

  // Stop typing when switching conversations or unmounting
  useEffect(() => {
    return () => {
      // Only clear the timeout if it belongs to this conversation
      if (typingConvIdRef.current === conversationId && typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
        typingConvIdRef.current = null;
      }
      if (isTypingRef.current) {
        stopTyping(conversationId);
        isTypingRef.current = false;
      }
    };
  }, [conversationId, stopTyping]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (gifSearchTimeoutRef.current) {
        clearTimeout(gifSearchTimeoutRef.current);
      }
    };
  }, []);

  // Cleanup object URLs when they change
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Process dropped files sequentially
  useEffect(() => {
    if (!droppedFiles || droppedFiles.length === 0) return;

    let cancelled = false;

    const processFiles = async () => {
      for (const file of droppedFiles) {
        if (cancelled) break;

        // Show preview for current file
        setSelectedFile(file);
        if (file.type.startsWith('image/')) {
          setPreviewUrl(URL.createObjectURL(file));
        } else {
          setPreviewUrl(null);
        }

        await uploadFile(file);
      }

      if (!cancelled) {
        clearSelectedFile();
        onDroppedFilesProcessed?.();
      }
    };

    processFiles();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when droppedFiles changes
  }, [droppedFiles]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    const pos = e.target.selectionStart;
    setContent(newContent);
    setCursorPosition(pos);
    handleTyping();

    // Check for @mention trigger
    const textBeforeCursor = newContent.slice(0, pos);
    const mentionMatch = textBeforeCursor.match(/@([^\s@]*)$/);
    if (mentionMatch) {
      setShowMentions(true);
      setMentionQuery(mentionMatch[1]);
      setMentionIndex(0);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  const insertMention = (mentionUser: User) => {
    const textBeforeCursor = content.slice(0, cursorPosition);
    const textAfterCursor = content.slice(cursorPosition);
    const mentionStart = textBeforeCursor.lastIndexOf('@');
    const newContent = textBeforeCursor.slice(0, mentionStart) + `@${mentionUser.name} ` + textAfterCursor;
    setContent(newContent);
    setMentionedUsers(prev => new Map(prev).set(mentionUser.id, { id: mentionUser.id, name: mentionUser.name }));
    setShowMentions(false);
    setMentionQuery('');
    textareaRef.current?.focus();
  };


  const sendGif = (gif: GifResult) => {
    sendMessage({
      conversation_id: conversationId,
      content: gif.url,
      type: 'text',
      metadata: {
        gif: {
          id: gif.id,
          title: gif.title,
          url: gif.url,
          preview_url: gif.preview_url,
        },
      },
    });
    setShowGifPicker(false);
    setGifSearch('');
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      recordingCancelledRef.current = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Always stop media tracks to release the microphone
        stream.getTracks().forEach((track) => track.stop());

        // If the recording was cancelled, don't populate blob/URL state
        if (recordingCancelledRef.current) {
          return;
        }

        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingDuration(0);

      // Update duration every second
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Microphone Access Denied', 'Could not access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    // Mark as cancelled BEFORE calling stop() so the async onstop handler
    // knows not to populate audioBlob / audioUrl state.
    recordingCancelledRef.current = true;
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    setRecordingDuration(0);
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob || !activeTenant) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      const fileName = `voice-message-${Date.now()}.webm`;
      formData.append('file', audioBlob, fileName);

      const response = await axios.post(
        `${API_URL}/api/messaging/conversations/${conversationId}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': activeTenant.id,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.message) {
        addMessage(conversationId, response.data.message);
      }

      // Clear audio state
      setAudioBlob(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      setRecordingDuration(0);
    } catch (error) {
      console.error('Failed to send voice message:', error);
      toast.error('Upload Failed', 'Failed to send voice message. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get scheduled messages for this conversation
  const conversationScheduledMessages = scheduledMessages.filter(
    (m) => m.conversationId === conversationId
  );

  // Schedule a message
  const handleScheduleMessage = () => {
    const trimmedContent = content.trim();
    if (!trimmedContent || !scheduledTime) return;

    const scheduledDate = new Date(scheduledTime);
    if (scheduledDate <= new Date()) {
      toast.warning('Invalid Time', 'Please select a future time');
      return;
    }

    addScheduledMessage({
      conversationId,
      content: trimmedContent,
      scheduledAt: scheduledDate.toISOString(),
    });

    setContent('');
    clearDraft(conversationId);
    setShowSchedulePicker(false);
    setScheduledTime('');
  };

  // Get minimum datetime for picker (now + 1 minute).
  //
  // WHY toDateTimeLocal AND NOT toISOString(): the `min` of an
  // `<input type="datetime-local">` carries NO timezone — the browser compares it
  // against the operator's LOCAL wall clock. Building it from toISOString() hands
  // the UTC wall clock to a local-time field, so the floor is off by the UTC
  // offset. West of UTC (every US zone) `min` sits 4-8h in the future, so after
  // the UTC date rollover (late afternoon in the Americas) the native calendar
  // greys out TODAY entirely and earlier same-day times render out-of-range — a
  // dispatcher trying to queue a shift-change note inside the current
  // operational period sees the picker refuse it. Worse, the picker lives inside
  // the message <form> (no noValidate) next to a type="submit" Send button, so an
  // out-of-range scheduled time blocks sending the message at all. East of UTC
  // `min` lands in the past and the guard silently does nothing.
  const getMinDateTime = () => toDateTimeLocal(new Date(Date.now() + 60_000));

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.warning('File Too Large', 'File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file: File): Promise<boolean> => {
    if (!activeTenant) return false;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/messaging/conversations/${conversationId}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': activeTenant.id,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadProgress(progress);
          },
        }
      );

      if (response.data.message) {
        addMessage(conversationId, response.data.message);
      }
      return true;
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Upload Failed', 'Failed to upload file. Please try again.');
      return false;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    const success = await uploadFile(selectedFile);
    if (success) {
      clearSelectedFile();
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (selectedFile) {
      await handleUpload();
      return;
    }

    const trimmedContent = content.trim();
    if (!trimmedContent) return;

    // Build mentions metadata from tracked mentioned users still present in content
    const activeMentions = Array.from(mentionedUsers.values()).filter(
      u => trimmedContent.includes(`@${u.name}`)
    );
    const metadata: Record<string, unknown> = {};
    if (activeMentions.length > 0) {
      metadata.mentions = activeMentions.map(u => ({ user_id: u.id, name: u.name }));
    }

    sendMessage({
      conversation_id: conversationId,
      content: trimmedContent,
      type: 'text',
      reply_to_id: replyMessage?.id,
      ...(Object.keys(metadata).length > 0 && { metadata }),
    });

    setContent('');
    setMentionedUsers(new Map());
    clearDraft(conversationId);
    clearReplyingTo(conversationId);
    isTypingRef.current = false;
    typingConvIdRef.current = null;
    stopTyping(conversationId);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle mention navigation
    if (showMentions && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(prev => (prev + 1) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(prev => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentionUsers[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setShowMentions(false);
        return;
      }
    }

    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }

    // Close pickers on Escape
    if (e.key === 'Escape') {
      setShowGifPicker(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return (
        <FaIcon icon="image" className="w-6 h-6" />
      );
    }
    if (fileType === 'application/pdf') {
      return (
        <FaIcon icon="file-lines" className="w-6 h-6" />
      );
    }
    return (
      <FaIcon icon="file-lines" className="w-6 h-6" />
    );
  };

  return (
    <div className="border-t border-theme-primary bg-surface-primary">
      {/* Reply Preview */}
      {replyMessage && (
        <div className="px-3 pt-3">
          <div className="flex items-center gap-2 p-2 bg-surface-tertiary rounded-lg border-l-4 border-primary-500">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-primary-600 dark:text-primary-400">
                Replying to {replyMessage.sender?.name || 'Unknown'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                {replyMessage.content}
              </p>
            </div>
            <IconButton
              icon={<FaIcon icon="xmark" className="w-4 h-4" />}
              label="Cancel reply"
              variant="ghost"
              size="sm"
              onClick={() => clearReplyingTo(conversationId)}
            />
          </div>
        </div>
      )}

      {/* File Preview */}
      {selectedFile && (
        <div className="px-3 pt-3">
          <div className="flex items-center gap-3 p-2 bg-surface-tertiary rounded-lg">
            {previewUrl ? (
              <img src={previewUrl} alt="Preview" className="w-12 h-12 object-cover rounded" loading="lazy" />
            ) : (
              <div className="w-12 h-12 flex items-center justify-center bg-gray-200 dark:bg-gray-600 rounded text-theme-tertiary">
                {getFileIcon(selectedFile.type)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-theme-primary truncate">{selectedFile.name}</p>
              <p className="text-xs text-theme-tertiary">{formatFileSize(selectedFile.size)}</p>
            </div>
            {!isUploading && (
              <IconButton
                icon={<FaIcon icon="xmark" className="w-5 h-5" />}
                label="Remove file"
                variant="ghost"
                size="sm"
                onClick={clearSelectedFile}
              />
            )}
          </div>
          {isUploading && (
            <div className="mt-2">
              <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                <div className="h-full bg-primary-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="text-xs text-theme-tertiary mt-1 text-center">Uploading... {uploadProgress}%</p>
            </div>
          )}
        </div>
      )}

      {/* Voice Recording Preview */}
      {(isRecording || audioBlob) && (
        <div className="px-3 pt-3">
          <div className="flex items-center gap-3 p-3 bg-surface-tertiary rounded-lg">
            {isRecording ? (
              <>
                {/* Recording indicator */}
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-theme-primary">
                    Recording... {formatDuration(recordingDuration)}
                  </span>
                </div>
                <IconButton
                  icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>}
                  label="Stop recording"
                  variant="primary"
                  size="sm"
                  onClick={stopRecording}
                  className="rounded-full"
                />
                <IconButton
                  icon={<FaIcon icon="xmark" className="w-5 h-5" />}
                  label="Cancel recording"
                  variant="ghost"
                  size="sm"
                  onClick={cancelRecording}
                />
              </>
            ) : audioBlob && audioUrl ? (
              <>
                {/* Audio preview */}
                <audio src={audioUrl} controls className="flex-1 h-10" />
                <span className="text-sm text-theme-tertiary">
                  {formatDuration(recordingDuration)}
                </span>
                <Button
                  icon={<FaIcon icon="paper-plane" className="w-5 h-5" />}
                  aria-label="Send voice message"
                  variant="primary"
                  size="sm"
                  loading={isUploading}
                  disabled={isUploading}
                  onClick={sendVoiceMessage}
                  className="rounded-full"
                />
                <IconButton
                  icon={<FaIcon icon="trash-can" className="w-5 h-5" />}
                  label="Discard recording"
                  variant="ghost"
                  size="sm"
                  onClick={cancelRecording}
                />
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Mention Autocomplete */}
      {showMentions && filteredMentionUsers.length > 0 && (
        <div className="px-3 pt-2">
          <div className="bg-surface-elevated rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
            {filteredMentionUsers.map((u, index) => (
              <Button
                key={u.id}
                variant="ghost"
                size="sm"
                onClick={() => insertMention(u)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left justify-start ${
                  index === mentionIndex
                    ? 'bg-primary-50 dark:bg-gray-700'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-500 flex items-center justify-center text-white text-xs font-medium">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm text-theme-primary">{u.name}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <form onSubmit={handleSubmit} className="flex items-end gap-2 p-3">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        />

        {/* Attachment button */}
        <IconButton
          icon={<FaIcon icon="paperclip" className="w-5 h-5" />}
          label="Attach file"
          variant="ghost"
          size="sm"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
          className="flex-shrink-0"
        />

        {/* GIF picker - only available when Tenor API key is configured */}
        {TENOR_API_KEY && (
        <div className="relative" ref={gifPickerRef}>
          <IconButton
            icon={<FaIcon icon="image" className="w-5 h-5" />}
            label="Add GIF"
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowGifPicker(!showGifPicker);
              setShowSchedulePicker(false);
            }}
            className="flex-shrink-0"
          />

          {showGifPicker && (
            <div className="absolute bottom-full mb-2 left-0 w-80 bg-surface-elevated rounded-lg shadow-xl border border-theme-primary overflow-hidden z-50">
              {/* Search input */}
              <div className="p-2 border-b border-theme-primary">
                <SearchInput
                  placeholder="Search GIFs..."
                  aria-label="Search GIFs"
                  value={gifSearch}
                  onChange={(e) => setGifSearch(e.target.value)}
                  className="pr-3 py-1.5 text-sm bg-surface-tertiary border-none"
                />
              </div>

              {/* GIF grid */}
              <div className="h-64 overflow-y-auto p-2">
                {isLoadingGifs ? (
                  <div className="flex items-center justify-center h-full">
                    <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                ) : gifs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-theme-tertiary">
                    <p className="text-sm">No GIFs found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {gifs.map((gif) => (
                      <Button
                        key={gif.id}
                        variant="ghost"
                        size="sm"
                        onClick={() => sendGif(gif)}
                        className="relative aspect-video bg-surface-tertiary rounded overflow-hidden hover:opacity-80 transition-opacity p-0 min-h-0"
                      >
                        <img
                          src={gif.preview_url}
                          alt={gif.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {/* Powered by Tenor */}
              <div className="px-2 py-1 text-xs text-theme-tertiary text-center border-t border-theme-primary">
                Powered by Tenor
              </div>
            </div>
          )}
        </div>
        )}

        {/* Schedule picker */}
        <div className="relative" ref={schedulePickerRef}>
          <IconButton
            icon={<FaIcon icon="clock" className="w-5 h-5" />}
            label="Schedule message"
            variant="ghost"
            size="sm"
            disabled={!content.trim()}
            onClick={() => {
              setShowSchedulePicker(!showSchedulePicker);
              setShowGifPicker(false);
            }}
            className="flex-shrink-0"
          />

          {showSchedulePicker && (
            <div className="absolute bottom-full mb-2 right-0 w-72 bg-surface-elevated rounded-lg shadow-xl border border-theme-primary overflow-hidden z-50">
              <div className="p-3 border-b border-theme-primary">
                <h4 className="text-sm font-medium text-theme-primary mb-2">Schedule Message</h4>
                <input aria-label="Scheduled send time"
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={getMinDateTime()}
                  className="w-full px-3 py-2 text-sm bg-surface-tertiary border-none rounded-lg focus:ring-2 focus:ring-primary-500 text-theme-primary"
                />
                <Button
                  variant="primary"
                  size="sm"
                  fullWidth
                  disabled={!scheduledTime || !content.trim()}
                  onClick={handleScheduleMessage}
                  className="mt-2"
                >
                  Schedule
                </Button>
              </div>

              {/* Scheduled messages list */}
              {conversationScheduledMessages.length > 0 && (
                <div className="p-2 max-h-40 overflow-y-auto">
                  <p className="text-xs text-theme-tertiary px-2 mb-1">Scheduled ({conversationScheduledMessages.length})</p>
                  {conversationScheduledMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className="flex items-start gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-theme-tertiary">
                          {new Date(msg.scheduledAt).toLocaleString()}
                        </p>
                        <p className="text-sm text-theme-primary truncate">{msg.content}</p>
                      </div>
                      <IconButton
                        icon={<FaIcon icon="xmark" className="w-4 h-4" />}
                        label="Cancel scheduled message"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeScheduledMessage(msg.id)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Text input */}
        <div className="flex-1 relative">
          <Input
            as="textarea"
            ref={textareaRef as React.Ref<HTMLTextAreaElement>}
            value={content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
            placeholder={selectedFile ? 'Add a caption...' : 'Type a message... (@ to mention)'}
            rows={1}
            disabled={isUploading}
            className="px-4 py-2 bg-surface-tertiary rounded-2xl resize-none border-none text-sm"
            style={{ maxHeight: '120px' }}
          />
        </div>

        {/* Voice message button */}
        {!content.trim() && !selectedFile && !audioBlob && (
          <IconButton
            icon={<FaIcon icon="microphone" className="w-5 h-5" />}
            label="Record voice message"
            variant="ghost"
            size="sm"
            disabled={isRecording || isUploading}
            onClick={startRecording}
            className="flex-shrink-0"
          />
        )}

        {/* Send button */}
        <Button
          icon={<FaIcon icon="paper-plane" className="w-5 h-5" />}
          aria-label="Send message"
          variant="primary"
          size="sm"
          type="submit"
          loading={isUploading}
          disabled={(!content.trim() && !selectedFile) || isUploading || isRecording || !!audioBlob}
          className="flex-shrink-0 rounded-full"
        />
      </form>
    </div>
  );
}
